/**
 * Pure helpers for TruInsight scope labels + greeting copy (DEF-269).
 *
 * Kept free of React so unit tests can cover every lens without mounting the panel.
 */

/** Dashboard lens fields the assistant cares about for copy + request body. */
export type AiChatLens = {
  mode: 'none' | 'company' | 'building' | 'user';
  companyId: number | null;
  companyName: string | null;
  buildingId: number | null;
  buildingName: string | null;
  scopeAsUserId: number | null;
  scopeAsUserName: string | null;
};

/** Request body fields sent with POST /api/ai/query and /api/ai/query/stream. */
export type AiChatScopeRequest = {
  scopeCompanyId: number | null;
  scopeBuildingId: number | null;
  scopeUserId: number | null;
};

/**
 * Derive the SSI request overrides from the active dashboard lens.
 * Non-SSI callers must pass `isSSI: false` — overrides are omitted so the backend pins to JWT scope.
 */
export function aiScopeRequestFor(isSSI: boolean, lens: AiChatLens): AiChatScopeRequest {
  if (!isSSI) {
    return { scopeCompanyId: null, scopeBuildingId: null, scopeUserId: null };
  }
  if (lens.mode === 'building' && lens.buildingId != null) {
    return {
      scopeCompanyId: null,
      scopeBuildingId: lens.buildingId,
      scopeUserId: null,
    };
  }
  if (lens.mode === 'company' && lens.companyId != null) {
    return {
      scopeCompanyId: lens.companyId,
      scopeBuildingId: null,
      scopeUserId: null,
    };
  }
  if (lens.mode === 'user' && lens.scopeAsUserId != null) {
    return {
      scopeCompanyId: null,
      scopeBuildingId: null,
      scopeUserId: lens.scopeAsUserId,
    };
  }
  return { scopeCompanyId: null, scopeBuildingId: null, scopeUserId: null };
}

/** Header "Answering for …" label for the active lens. */
export function scopeLabelFor(isSSI: boolean, lens: AiChatLens): string {
  if (!isSSI) return 'Your portfolio';
  if (lens.mode === 'user' && lens.scopeAsUserName) {
    return lens.scopeAsUserName;
  }
  if (lens.mode === 'building') {
    return lens.buildingName ?? (lens.buildingId != null ? `Building ${lens.buildingId}` : 'All companies');
  }
  if (lens.mode === 'company' && lens.companyName) {
    return lens.companyName;
  }
  return 'All companies';
}

/** Phrase woven into the welcome bubble ("Ask me anything about …"). */
export function greetingFocusFor(isSSI: boolean, lens: AiChatLens): string {
  if (!isSSI) return 'your portfolio';
  if (lens.mode === 'user' && lens.scopeAsUserName) {
    return `${lens.scopeAsUserName}'s portfolio`;
  }
  if (lens.mode === 'building') {
    const name = lens.buildingName ?? (lens.buildingId != null ? `building ${lens.buildingId}` : null);
    return name ? `${name}'s data` : 'every company in your portfolio';
  }
  if (lens.mode === 'company' && lens.companyName) {
    return `${lens.companyName}'s portfolio`;
  }
  return 'every company in your portfolio';
}

/** True when the SSI lens narrows away from the portfolio-wide view. */
export function isNarrowedLens(isSSI: boolean, lens: AiChatLens): boolean {
  if (!isSSI) return true;
  return lens.mode === 'company' || lens.mode === 'building' || lens.mode === 'user';
}
